import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getDefaultEmploymentFilters,
  getDefaultPagination,
  getDepartments,
  getEmployees,
  getEmploymentContracts,
  getEmploymentDashboardData,
  getEmploymentFilterOptions,
  getEmploymentHistory,
  getEmploymentStats,
  getEmploymentTypes,
  getWorkSchedules,
  updateEmployeeInDb,
} from "../services/employmentService";

const DEFAULT_EMPLOYEES_FALLBACK = [
  {
    uuid: "d783e38f-949f-11f1-827a-40e8d4bfd741",
    employee_id: "EMP-0001",
    full_name: "Raj Likhar",
    email: "raj.likhar@company.com",
    department_name: "Engineering",
    designation: "Software Developer",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Full-Time Permanent",
    work_schedule_name: "General Shift (09:00 - 18:00)",
    status: "Active",
    joining_date: "2026-01-01",
  },
  {
    uuid: "d783e38f-949f-11f1-827a-40e8d4bfd742",
    employee_id: "EMP-0002",
    full_name: "Amit Kumar Sharma",
    email: "amit.sharma@company.com",
    department_name: "Engineering",
    designation: "Backend Developer",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Full-Time Permanent",
    work_schedule_name: "General Shift (09:00 - 18:00)",
    status: "Active",
    joining_date: "2026-01-01",
  },
  {
    uuid: "d783e38f-949f-11f1-827a-40e8d4bfd743",
    employee_id: "EMP-0011",
    full_name: "Raj Kumar",
    email: "raj.kumar@company.com",
    department_name: "Human Resources",
    designation: "HR Manager",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Permanent Agreement",
    work_schedule_name: null,
    status: "Active",
    joining_date: "2026-08-11",
  },
];

// useEmployment: Central state and business logic for the Employment module.
const useEmployment = () => {
  // States
  const [filters, setFiltersState] = useState(getDefaultEmploymentFilters());
  const [pagination, setPagination] = useState(getDefaultPagination());
  const [employees, setEmployees] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [employmentContracts, setEmploymentContracts] = useState([]);
  const [workSchedules, setWorkSchedules] = useState([]);
  const [employmentHistory, setEmploymentHistory] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState(getDefaultStats());
  const [filterOptions, setFilterOptions] = useState({
    departments: [],
    employmentTypes: [],
    workSchedules: [],
    contractStatuses: [],
    employeeStatuses: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UPDATE PAGINATION FROM RESPONSE
  const updatePaginationFromResponse = useCallback(
    (response) => {
      const pag = response?.pagination ?? {};
      const totalItems =
        pag.totalItems ??
        pag.total_items ??
        pag.total ??
        response?.total ??
        (Array.isArray(response?.data) ? response.data.length : 0);
      const totalPages = Math.max(1, Math.ceil(totalItems / (pagination.pageSize || 10)));

      setPagination((previous) => ({
        ...previous,
        totalItems,
        totalPages,
      }));
    },
    [pagination.pageSize]
  );

  // LOAD ALL DATA
  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [
        empRes,
        typesRes,
        contractsRes,
        schedulesRes,
        historyRes,
        deptsRes,
        statsRes,
        dashboardRes,
        optionsRes,
      ] = await Promise.all([
        getEmployees(),
        getEmploymentTypes(),
        getEmploymentContracts(),
        getWorkSchedules(),
        getEmploymentHistory(),
        getDepartments(),
        getEmploymentStats(),
        getEmploymentDashboardData(),
        getEmploymentFilterOptions(),
      ]);

      let empList = extractArray(empRes);
      if (empList.length === 0) {
        empList = DEFAULT_EMPLOYEES_FALLBACK;
      }
      empList.sort((a, b) => {
        const idA = a.employee_id || a.employee_code || "";
        const idB = b.employee_id || b.employee_code || "";
        return idA.localeCompare(idB, undefined, { numeric: true, sensitivity: "base" });
      });
      setEmployees(empList);
      updatePaginationFromResponse(empRes);

      const rawTypes = extractArray(typesRes);
      const activeTypes = rawTypes.length > 0 ? rawTypes : getEmploymentTypes();
      
      const tList = (Array.isArray(activeTypes) ? activeTypes : []).map(type => {
        const count = empList.filter(emp => {
          const empText = `${emp.employment_type_name || ""} ${emp.contract_type || ""} ${emp.remarks || ""} ${emp.designation || ""}`.toLowerCase();
          const nameKey = (type.type_name || "").toLowerCase().trim();
          const codeKey = (type.type_code || "").toLowerCase().trim();

          if (nameKey && empText.includes(nameKey)) return true;
          if (codeKey && (empText.includes(codeKey) || (codeKey === "ft" && empText.includes("full")))) return true;
          if (nameKey.includes("full") && (empText.includes("full") || empText.includes("permanent"))) return true;
          if (nameKey.includes("part") && empText.includes("part")) return true;
          if (nameKey.includes("intern") && empText.includes("intern")) return true;
          if (nameKey.includes("contract") && empText.includes("contract")) return true;
          return false;
        }).length;
        
        return {
          ...type,
          employee_count: count > 0 ? count : (type.employee_count || 0)
        };
      });
      setEmploymentTypes(tList);
      
      const cList = extractArray(contractsRes);
      setEmploymentContracts(cList);
      
      const sList = extractArray(schedulesRes);
      setWorkSchedules(sList);
      
      const hList = extractArray(historyRes);
      setEmploymentHistory(hList);
      
      const dList = extractArray(deptsRes);
      setDepartments(dList);

      // Compute dynamic stats from DB
      let computedStats = {
        totalEmployees: empRes?.total ?? empList.length,
        activeEmploymentTypes: tList.filter(t => t.status?.toLowerCase() === 'active').length || tList.length,
        activeContracts: cList.filter(c => c.status?.toLowerCase() === 'active').length || cList.length,
        employmentChanges: hList.length
      };

      // Use the real stats API if it has data, otherwise use our calculated stats
      const backendStats = normalizeStats(statsRes);
      const finalStats = (backendStats.totalEmployees === 0 && computedStats.totalEmployees > 0)
        ? computedStats
        : backendStats;

      setStats(finalStats);

      const options = optionsRes?.data || optionsRes || {};
      setFilterOptions({
        departments: options.departments || deptsRes || [],
        employmentTypes: options.employmentTypes || [],
        workSchedules: options.workSchedules || [],
        contractStatuses: options.contractStatuses || [],
        employeeStatuses: options.employeeStatuses || [],
      });
    } catch (err) {
      console.error("Failed to load employment data:", err);
      setError(err?.message || "Failed to load employment data.");
    } finally {
      setLoading(false);
    }
  }, [updatePaginationFromResponse]);

  // INITIAL LOAD
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // UPDATE FILTERS
  const updateFilters = useCallback((newFilters) => {
    setFiltersState((previous) => ({
      ...previous,
      ...newFilters,
    }));
    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  }, []);

  // RESET FILTERS
  const resetFilters = useCallback(() => {
    setFiltersState(getDefaultEmploymentFilters());
    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  }, []);

  // NEXT PAGE
  const nextPage = useCallback(() => {
    setPagination((previous) => {
      const totalPages = previous.totalPages || 1;
      if (previous.page >= totalPages) return previous;
      return { ...previous, page: previous.page + 1 };
    });
  }, []);

  // PREVIOUS PAGE
  const previousPage = useCallback(() => {
    setPagination((previous) => {
      if (previous.page <= 1) return previous;
      return { ...previous, page: previous.page - 1 };
    });
  }, []);

  // GO TO PAGE
  const goToPage = useCallback((page) => {
    setPagination((previous) => {
      const totalPages = previous.totalPages || 1;
      const nextPageNumber = Math.min(Math.max(1, page), totalPages);
      return { ...previous, page: nextPageNumber };
    });
  }, []);

  // CHANGE PAGE SIZE
  const changePageSize = useCallback((pageSize) => {
    setPagination((previous) => ({
      ...previous,
      page: 1,
      pageSize,
    }));
  }, []);

  // REFRESH
  const refresh = useCallback(async () => {
    await loadAllData();
  }, [loadAllData]);

  // FILTERED EMPLOYEES
  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      if (filters.search) {
        const query = filters.search.toLowerCase();
        const matchesName = employee.full_name?.toLowerCase().includes(query) ||
                            employee.first_name?.toLowerCase().includes(query) ||
                            employee.last_name?.toLowerCase().includes(query);
        const matchesId = employee.employee_id?.toLowerCase().includes(query);
        const matchesEmail = employee.email?.toLowerCase().includes(query);
        const matchesDesignation = employee.designation?.toLowerCase().includes(query);
        const matchesDepartment = employee.department_name?.toLowerCase().includes(query);
        const matchesContract = employee.contract_type?.toLowerCase().includes(query);
        if (!matchesName && !matchesId && !matchesEmail && !matchesDesignation && !matchesDepartment && !matchesContract) {
          return false;
        }
      }
      if (filters.department && filters.department !== "all") {
        const deptVal = String(filters.department).toLowerCase();
        const empDeptName = String(employee.department_name || "").toLowerCase();
        const empDeptUuid = String(employee.department_uuid || "").toLowerCase();
        if (empDeptUuid !== deptVal && empDeptName !== deptVal && !empDeptName.includes(deptVal) && !deptVal.includes(empDeptName)) return false;
      }
      if (filters.employmentType && filters.employmentType !== "all") {
        const typeVal = String(filters.employmentType).toLowerCase();
        const empTypeName = String(employee.employment_type_name || "").toLowerCase();
        const empTypeUuid = String(employee.employment_type_uuid || "").toLowerCase();
        if (empTypeUuid !== typeVal && empTypeName !== typeVal && !empTypeName.includes(typeVal) && !typeVal.includes(empTypeName)) return false;
      }
      if (filters.contractStatus && filters.contractStatus !== "all") {
        const statusVal = (employee.status || "").toLowerCase();
        const filterVal = filters.contractStatus.toLowerCase();
        if (filterVal === "active" && statusVal !== "active") return false;
        if (filterVal === "expiring soon" && (employee.days_left === null || employee.days_left > 60 || employee.days_left <= 0)) return false;
        if (filterVal === "expired" && (employee.days_left === null || employee.days_left > 0)) return false;
      }
      if (filters.workSchedule && filters.workSchedule !== "all") {
        const schedVal = String(filters.workSchedule).toLowerCase();
        const empSchedName = String(employee.work_schedule_name || "").toLowerCase();
        const empSchedUuid = String(employee.work_schedule_uuid || "").toLowerCase();
        if (empSchedUuid !== schedVal && empSchedName !== schedVal && !empSchedName.includes(schedVal) && !schedVal.includes(empSchedName)) return false;
      }
      if (filters.status && filters.status !== "all") {
        if ((employee.status || "").toLowerCase() !== filters.status.toLowerCase()) return false;
      }
      return true;
    }).sort((a, b) => {
      const idA = a.employee_id || a.employee_code || "";
      const idB = b.employee_id || b.employee_code || "";
      return idA.localeCompare(idB, undefined, { numeric: true, sensitivity: "base" });
    });
  }, [employees, filters, employmentContracts]);

  // SYNC PAGINATION WITH FILTERED COUNT
  useEffect(() => {
    const totalItems = filteredEmployees.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / (pagination.pageSize || 10)));
    setPagination((prev) => ({
      ...prev,
      totalItems,
      totalPages,
      page: Math.min(prev.page, totalPages) || 1,
    }));
  }, [filteredEmployees.length, pagination.pageSize]);

  // EXPORT
  const handleExport = useCallback(() => {
    if (filteredEmployees.length === 0) {
      alert("No data available to export.");
      return;
    }

    const headers = [
      "Employee ID", "Full Name", "Email", "Department", "Designation",
      "Employment Type", "Work Schedule", "Joining Date", "Status", "Manager Name"
    ];

    const rows = filteredEmployees.map((employee) => [
      employee.employee_id || "",
      employee.full_name || "",
      employee.email || "",
      employee.department_name || "",
      employee.designation || "",
      employee.employment_type_name || "",
      employee.work_schedule_name || "",
      employee.joining_date || "",
      employee.status || "",
      employee.manager_name || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((value) => {
          const stringValue = String(value);
          if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(",")
      )
    ].join("\n");

    try {
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `employment_records_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Export to CSV failed:", error);
    }
  }, [filteredEmployees]);

  // PAGINATED EMPLOYEES
  const paginatedEmployees = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredEmployees.slice(start, end);
  }, [filteredEmployees, pagination.page, pagination.pageSize]);

  // SAVE EMPLOYMENT TYPE
  const saveEmploymentType = useCallback((payload, uuid) => {
    setEmploymentTypes((prev) => {
      if (uuid) {
        return prev.map((item) => item.uuid === uuid ? { ...item, ...payload } : item);
      } else {
        const newType = {
          uuid: `type_${Math.random().toString(36).substr(2, 9)}`,
          ...payload,
          status: payload.status || "active",
        };
        setStats((prevStats) => ({
          ...prevStats,
          activeEmploymentTypes: prevStats.activeEmploymentTypes + 1,
        }));
        return [...prev, newType];
      }
    });
  }, []);

  // UPDATE EMPLOYEE (API Persist + State Update)
  const updateEmployee = useCallback(async (uuid, payload) => {
    if (uuid) {
      await updateEmployeeInDb(uuid, payload);
    }

    setEmployees((prev) =>
      prev.map((emp) => {
        const isMatch =
          (uuid && (emp.uuid === uuid || emp.employee_id === uuid)) ||
          (payload.full_name && emp.full_name === payload.full_name) ||
          (payload.email && emp.email === payload.email);

        if (isMatch) {
          const updatedStatus = payload.status
            ? payload.status.charAt(0).toUpperCase() + payload.status.slice(1).toLowerCase()
            : emp.status;
          return {
            ...emp,
            ...payload,
            status: updatedStatus,
          };
        }
        return emp;
      })
    );
  }, []);

  // DELETE EMPLOYEE
  const deleteEmployee = useCallback((uuid) => {
    setEmployees((prev) =>
      prev.filter((emp) => emp.uuid !== uuid && emp.employee_id !== uuid)
    );
    setStats((prevStats) => ({
      ...prevStats,
      totalEmployees: Math.max(0, prevStats.totalEmployees - 1),
    }));
  }, []);

  return {
    employees,
    paginatedEmployees,
    employmentTypes,
    employmentContracts,
    workSchedules,
    employmentHistory,
    departments,
    stats,
    filters,
    filterOptions,
    setFilters: updateFilters,
    updateFilters,
    resetFilters,
    pagination,
    nextPage,
    previousPage,
    goToPage,
    changePageSize,
    loading,
    error,
    refresh,
    handleExport,
    saveEmploymentType,
    updateEmployee,
    deleteEmployee,
  };
};

const extractArray = (res) => Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : (Array.isArray(res?.items) ? res.items : (Array.isArray(res?.results) ? res.results : [])));
const normalizeStats = (res) => {
  const d = res?.data || res || {};
  return { totalEmployees: d.totalEmployees ?? d.total_employees ?? 0, activeEmploymentTypes: d.activeEmploymentTypes ?? d.active_employment_types ?? 0, activeContracts: d.activeContracts ?? d.active_contracts ?? 0, employmentChanges: d.employmentChanges ?? d.employment_changes ?? 0 };
};
const calculateTotalPages = (items, size) => (!items || !size) ? 1 : Math.max(1, Math.ceil(items / size));
const getDefaultStats = () => ({ totalEmployees: 0, activeEmploymentTypes: 0, activeContracts: 0, employmentChanges: 0 });

export default useEmployment;